// Couvre-feu de publication — pendant la tranche horaire définie par l'API, publier est réservé
// aux profils certifiés (et aux abonnés / au staff). Miroir de
// services/publicationCurfew/publicationCurfew.service.js (voissa-api-rest).
//
// RÉPARTITION DES RÔLES — l'API sert les BORNES et le VERDICT d'exemption dans `currentUser`
// (`publicationCurfew`, cf. GET /users/me) ; ce module ne fait que comparer l'heure courante à ces
// bornes. Rien de la règle métier n'est donc réécrit ici : régler la fenêtre sur 20:30 côté API
// suffit, le front suit sans qu'on touche à ce fichier.
//
// Pourquoi l'heure est calculée ici et pas servie par l'API : `currentUser` vit en cache React
// Query, parfois depuis des heures. Un booléen « couvre-feu actif » serait périmé au premier
// passage de la borne. L'heure, elle, se recalcule à chaque rendu.
//
// L'API reste seule juge : elle refuse en 403 avec le code ci-dessous, et le front n'anticipe ce
// refus que pour ouvrir la bonne modale — celle de la certification, gratuite, et surtout pas
// celle de l'abonnement.
import moment from 'moment-timezone';

// Contrat avec l'API : `errors.code` du payload 403.
export const PUBLICATION_CURFEW_ERROR_CODE = 'PUBLICATION_CURFEW';

// Repli utilisé UNIQUEMENT tant que le payload de l'API n'est pas là : première frame (GET
// /users/me en vol), session ouverte avant la mise en service, front déployé avant l'API.
//
// Ses horaires ne DÉCIDENT rien — `enabled: false` veut dire « sans information, on ne bloque pas,
// l'API tranchera et le filet global ouvrira la modale ». Ils servent à l'AFFICHAGE : sans eux, une
// modale ouverte par un 403 sans bornes dirait « Entre  et  », et un `timezone` absent ferait
// planter le `.format()` de moment (`moment().tz(undefined)` vaut `undefined`).
const CURFEW_DISPLAY_FALLBACK = {
    enabled: false,
    start: '20:00',
    end: '08:00',
    timezone: 'Europe/Paris',
    exempt: false,
};

/**
 * Réglage applicable, par ordre de fraîcheur croissante :
 *   1. le repli ci-dessus (20h → 8h) si on ne sait rien ;
 *   2. ce que l'API a servi dans `currentUser` (GET /users/me) ;
 *   3. `override` — les bornes portées par un refus 403, donc exactes à la seconde près. C'est la
 *      seule source juste quand le cache `currentUser` date d'avant un changement de fenêtre.
 */
export const getPublicationCurfewConfig = (currentUser, override) => ({
    ...CURFEW_DISPLAY_FALLBACK,
    ...(currentUser?.publicationCurfew || {}),
    ...(override || {}),
});

/** 'HH:mm' -> minutes depuis minuit. Renvoie null si la borne est illisible (on ne bloque alors pas). */
const parseCurfewTime = (value) => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value ?? ''));
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/** 'HH:mm' -> libellé affiché : '20:30' → « 20h30 », '08:00' → « 8h ». */
export const formatCurfewTime = (value) => {
    const [hours, minutes] = String(value ?? '').split(':');
    if (Number.isNaN(Number(hours))) return '';
    return Number(minutes) === 0 ? `${Number(hours)}h` : `${Number(hours)}h${minutes}`;
};

/**
 * La fenêtre est-elle ouverte à cet instant ? C'est le rapport entre les bornes qui dit si elle
 * traverse minuit (start > end) ou tient dans la journée — aucun réglage supplémentaire.
 */
export const isPublicationCurfewActive = (currentUser, at = new Date()) => {
    const config = getPublicationCurfewConfig(currentUser);
    if (!config.enabled) return false;

    const start = parseCurfewTime(config.start);
    const end = parseCurfewTime(config.end);
    if (start === null || end === null || start === end) return false;

    const now = moment(at).tz(config.timezone);
    const minutes = now.hour() * 60 + now.minute();
    return start > end ? (minutes >= start || minutes < end) : (minutes >= start && minutes < end);
};

/** Le membre est-il empêché de publier à cet instant ? (`exempt` vient du serveur.) */
export const isPublicationCurfewBlocked = (currentUser, at = new Date()) =>
    !getPublicationCurfewConfig(currentUser).exempt && isPublicationCurfewActive(currentUser, at);

/**
 * Prochaine réouverture (la prochaine borne de fin), pour l'annoncer au membre.
 * Prend la CONFIG (cf. getPublicationCurfewConfig) et non l'utilisateur : l'appelant a parfois une
 * source plus fraîche que son cache, et c'est elle qui doit primer.
 */
export const publicationCurfewEndsAt = (config, at = new Date()) => {
    const end = parseCurfewTime(config?.end);
    if (end === null) return null;

    const now = moment(at).tz(config.timezone || CURFEW_DISPLAY_FALLBACK.timezone);
    const endsAt = now.clone().startOf('day').add(end, 'minutes');
    if (!endsAt.isAfter(now)) endsAt.add(1, 'day');
    return endsAt.toDate();
};

/**
 * Reconnaît le refus de l'API, quelle que soit la forme de l'erreur reçue : mutation
 * (`error.response.errors`), query (`error.errors`) ou axios brut (`error.response.data`).
 * Cf. src/interceptors/data.js, qui normalise différemment les deux premiers cas.
 */
export const isPublicationCurfewError = (error) => {
    const codes = [
        error?.errors?.code,
        error?.response?.errors?.code,
        error?.response?.data?.errors?.code,
    ];
    return codes.includes(PUBLICATION_CURFEW_ERROR_CODE);
};

// --- Filet de sécurité global -----------------------------------------------------------------
// Tous les CTA de publication ne sont pas gatés au clic (éditeurs, formulaires). Quand l'API refuse
// malgré tout — chemin non gaté, cache `currentUser` périmé, ou borne franchie pendant la saisie —
// le MutationCache émet ici, et le listener monté sous le ModalManager ouvre la même modale. Un
// abonné module-level : l'émetteur (cache React Query) vit hors de l'arbre React et n'a pas accès
// au contexte des modales.
let listeners = [];

export const subscribePublicationCurfew = (listener) => {
    listeners = [...listeners, listener];
    return () => {
        listeners = listeners.filter((item) => item !== listener);
    };
};

export const emitPublicationCurfew = (payload = {}) => {
    listeners.forEach((listener) => listener(payload));
};
